import {isStaff} from '@github-ui/stats'
import {copilotFeatureFlags} from '@github-ui/copilot-feature-flags'
import {useSyncExternalStore} from 'react'

/**
 * Staff-only override for the sandbox image Mission Control boots new Cloud sessions with. The value is a plain image
 * UUID sent as `compute.image` on `POST /agents/tasks`.
 */
export const SANDBOX_IMAGE_OVERRIDE_KEY = 'COPILOT_SANDBOX_IMAGE_OVERRIDE_v1'

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

const listeners = new Set<() => void>()

export function isValidSandboxImageId(value: string): boolean {
  return UUID_PATTERN.test(value.trim())
}

function readStoredValue(): string | undefined {
  try {
    const stored = localStorage.getItem(SANDBOX_IMAGE_OVERRIDE_KEY)?.trim()
    return stored && isValidSandboxImageId(stored) ? stored : undefined
  } catch {
    return undefined
  }
}

function canOverrideSandboxImage(): boolean {
  return isStaff() && copilotFeatureFlags.sandboxImageOverride
}

/** The override to send with a new task, or undefined when none is set or the viewer is not eligible. */
export function readSandboxImageOverride(): string | undefined {
  if (typeof window === 'undefined' || !canOverrideSandboxImage()) return undefined
  return readStoredValue()
}

export function setSandboxImageOverride(image: string | undefined): void {
  const trimmed = image?.trim()
  if (trimmed && !isValidSandboxImageId(trimmed)) throw new Error('Sandbox image must be a UUID.')
  if (trimmed) localStorage.setItem(SANDBOX_IMAGE_OVERRIDE_KEY, trimmed)
  else localStorage.removeItem(SANDBOX_IMAGE_OVERRIDE_KEY)
  for (const listener of listeners) listener()
}

export function subscribeToSandboxImageOverride(callback: () => void): () => void {
  const handleCrossTab = (event: StorageEvent) => {
    // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
    if (event.key === SANDBOX_IMAGE_OVERRIDE_KEY || event.key === null) callback()
  }
  listeners.add(callback)
  window.addEventListener('storage', handleCrossTab)
  return () => {
    listeners.delete(callback)
    window.removeEventListener('storage', handleCrossTab)
  }
}

const getServerSnapshot = () => undefined
const getStaffServerSnapshot = () => false
const subscribeToNothing = () => () => {}

export interface SandboxImageOverrideState {
  /** False on the server, for non-staff viewers, and when the image override flag is off. */
  canOverride: boolean
  image: string | undefined
  setImage: (image: string | undefined) => void
}

export function useSandboxImageOverride(): SandboxImageOverrideState {
  const canOverride = useSyncExternalStore(subscribeToNothing, canOverrideSandboxImage, getStaffServerSnapshot)
  const image = useSyncExternalStore(subscribeToSandboxImageOverride, readSandboxImageOverride, getServerSnapshot)
  return {canOverride, image, setImage: setSandboxImageOverride}
}
